import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'next-i18next';

import MetaData from 'src/config/MetaData';
import RoutePaths from 'src/config/RoutePaths';
import ArticlePostModel from 'src/types/article/ArticlePostModel';
import LayoutWrapper from 'src/shared/components/LayoutWrapper';
import Section from 'src/shared/components/Section';
import TitleText from 'src/shared/components/TitleText';
import Image from 'src/shared/components/Image';
import BackButton from 'src/components/BackButton';
import ShareButtons from 'src/components/ShareButtons';

import ArticleContent from './components/ArticleContent';
import styles from './styles.module.scss';
import useArticleInfo from 'src/hooks/useArticleInfo';
import useResize from 'src/shared/hooks/useResize';
import BREAKPOINT from 'src/shared/config/Breakpoint';
import DiscoverMore from 'src/components/DiscoverMore';
import RelatedArticles from './components/RelatedArticles';
import FundCard from './components/FundCard';
import ContactSalesCard from './components/ContactSalesCard';
import SupportCenterCard from './components/SupportCenterCard';
import useMemberInfo from 'src/shared/hooks/useMemberInfo';
import { MEDIA_MKT_LABEL } from 'src/config/MediaCategory';
import { useQuery } from 'react-query';
import ArticleAPI from 'src/services/api/ArticleAPI';
import common from 'src/shared/utils/common';

// eslint-disable-next-line prefer-destructuring
const FACEBOOK_PAGE = process.env.FACEBOOK_PAGE;
// eslint-disable-next-line prefer-destructuring
const FACEBOOK_APP_ID = process.env.FACEBOOK_APP_ID;
// eslint-disable-next-line prefer-destructuring
const FACEBOOK_PAGE_ID = process.env.FACEBOOK_PAGE_ID;

const homeInfo = MetaData[RoutePaths.HOME];

interface Props {
    category: string;
    articleUrl: string;
    article: ArticlePostModel;
}

// @TODO update og
const NewsDetail: React.FC<Props> = (props) => {
    const { article, category } = props;
    const pageTitle = `Altive | ${article.title}`;
    const seoFields = article.seoField ? JSON.parse(article.seoField) : {};

    const memberInfo = useMemberInfo();

    const { i18n, t } = useTranslation();
    const { screenSize } = useResize();
    const { infoText } = useArticleInfo(article);
    const [urls, setUrls] = useState({ share: '', back: '', article: '' });
    const isSideBarShown = screenSize.width > BREAKPOINT.LG;
    const isBackShownOnHeader = screenSize.width <= BREAKPOINT.MD;
    const isFundCardShown = !memberInfo.isLoggedIn && article.showFundCard;
    const isContactSalesCardShown = !memberInfo.isLoggedIn;
    const isSupportCenterCardShown = memberInfo.isLoggedIn;

    let metaArr = [
        { key: 'author', name: 'author', content: article.author },
        { key: 'publisher', name: 'publisher', content: article.author },
        { key: 'og:image:alt', property: 'og:image:alt', content: article.coverImageAltTag },
        { key: 'article:published_time', property: 'article:published_time', content: new Date(article.publishedAt) },
        { key: 'article:modified_time', property: 'article:modified_time', content: new Date(article.updatedAt) },

        { key: 'twitter:card', property: 'twitter:card', content: 'summary_large_image' },
        { key: 'twitter:description', property: 'twitter:description', content: homeInfo.description.en },
        { key: 'twitter:site', property: 'twitter:site', content: article.author },
        { key: 'twitter:url', property: 'twitter:url', content: urls.article },
        { key: 'twitter:image', property: 'twitter:image', content: article.coverImage },
        { key: 'twitter:image:alt', property: 'twitter:image:alt', content: homeInfo.description.en },
        { key: 'twitter:title', property: 'twitter:title', content: pageTitle },
        { key: 'twitter:creator', property: 'twitter:creator', content: article.author },
    ];
    metaArr = metaArr.concat(article.tags.map((x) => ({ key: 'article:tag', property: 'article:tag', content: x.tagName })));

    if (FACEBOOK_PAGE) {
        metaArr.push({ key: 'article:publisher', property: 'article:publisher', content: FACEBOOK_PAGE });
        metaArr.push({ key: 'article:author', property: 'article:author', content: FACEBOOK_PAGE });
    }
    if (FACEBOOK_APP_ID) {
        metaArr.push({ key: 'fb:app_id', property: 'fb:app_id', content: FACEBOOK_APP_ID });
    }
    if (FACEBOOK_PAGE_ID) {
        metaArr.push({ key: 'fb:oages', property: 'fb:oages', content: FACEBOOK_PAGE_ID });
    }

    const articleGroupQuery = useQuery(ArticleAPI.getArticleGroup.key, () => ArticleAPI.getArticleGroup({ groupId: article.groupId }));

    const getAsPath = useCallback(
        (lang: string) => {
            let route = common.getAsPath(RoutePaths.NEWS_DETAIL, { category, articleUrl: props.article.articleUrl });
            if (articleGroupQuery.isFetched) {
                const correspondingArticle = articleGroupQuery.data[lang];
                if (correspondingArticle && correspondingArticle.articleId !== props.article.articleId) {
                    route = common.getAsPath(RoutePaths.NEWS_DETAIL, { category, articleUrl: correspondingArticle.articleUrl });
                    // router.replacePath(RoutePaths.NEWS_DETAIL, { category, articleUrl: correspondingArticle.articleUrl });
                }
                if (!correspondingArticle) {
                    route = common.getAsPath(RoutePaths.NEWS_LIST, { category });
                }
            }
            return route.as;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [articleGroupQuery.isFetched]
    );

    useEffect(() => {
        const baseUrl = window.location.origin;
        setUrls({
            share: window.location.href,
            back: `${baseUrl}/${i18n.language}/media/${props.category}`,
            article: `${baseUrl}/${i18n.language}/media/${props.category}/${props.articleUrl}`,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <LayoutWrapper
            className={styles.container}
            headerProps={{
                type: 'black',
                shouldShowBack: isBackShownOnHeader,
                backUrl: urls.back,
                fixed: true,
            }}
            footerProps={{ getAsPath }}
            metaProps={{
                type: 'article',
                title: `Altive | ${article.title}`,
                description: article.excerpt,
                keywords: seoFields.keywords,
                coverImage: article.coverImage,
                url: urls.article,
                customMetaArr: metaArr,
                article,
            }}
        >
            <div className={styles.page}>
                <Section containerClassName={styles.header} className={styles.section} isArticle>
                    <div className={styles.content}>
                        <div className={styles.mainColumn}>
                            {!isBackShownOnHeader && <BackButton className={styles.backButton} link={urls.back} />}
                            <TitleText tag="h1" className={styles.title}>
                                {article.title}
                            </TitleText>
                            <div className={styles.infoAndShareButtons}>
                                <div className={styles.infoText}>{infoText}</div>
                                <ShareButtons shareTitle={`${article.title}`} url={urls.share} isLabelShown />
                            </div>
                            <div className={styles.cover_image}>
                                <Image src={article.coverImage} alt={article.coverImageAltTag} />
                            </div>
                            <ArticleContent content={article.articleContents} />
                            {!!article.tags.length && (
                                <DiscoverMore className={styles.discoverMore} title={t('article_discover_more_title')} tags={article.tags} />
                            )}
                            {!isSideBarShown && (
                                <>
                                    {isFundCardShown && <FundCard className={styles.card} themeColor={article.themeColor} isMasked />}
                                    {isContactSalesCardShown && <ContactSalesCard className={styles.card} />}
                                    {isSupportCenterCardShown && <SupportCenterCard className={styles.card} />}
                                </>
                            )}
                            {article.relatedArticles && !!article.relatedArticles.length && (
                                <RelatedArticles className={styles.relatedArticles} articles={article.relatedArticles} />
                            )}
                        </div>
                        {isSideBarShown && (
                            <div className={styles.sideColumn}>
                                {isFundCardShown && <FundCard className={styles.card} themeColor={article.themeColor} isMasked />}
                                {isContactSalesCardShown && <ContactSalesCard className={styles.card} gaLabel={MEDIA_MKT_LABEL[props.category]} />}
                                {isSupportCenterCardShown && <SupportCenterCard className={styles.card} />}
                            </div>
                        )}
                    </div>
                </Section>
            </div>
        </LayoutWrapper>
    );
};

export default NewsDetail;
